import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { deal, shuffle, standardDeck, type Card, type Rank } from '@campfire/cards';

export interface OldMaidOptions {
  /** Card id removed before dealing; its partner becomes the Old Maid. */
  readonly removedCardId: string;
}

export interface DrawEvent {
  readonly drawer: Seat;
  readonly from: Seat;
  /** Visible only to the drawer and the player it was taken from, unless it formed a pair. */
  readonly card: Card;
  readonly paired: Rank | null;
}

export interface OldMaidState {
  /** Hand order is the face-down order offered to the next drawer. */
  readonly hands: readonly (readonly Card[])[];
  readonly discards: readonly Card[];
  readonly turn: Seat;
  readonly last: DrawEvent | null;
  readonly result: Outcome | null;
}

export type OldMaidAction = { readonly type: 'draw'; readonly index: number };

export interface OldMaidView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  readonly handCounts: readonly number[];
  readonly discards: readonly Card[];
  readonly turn: Seat;
  /** The seat the active player draws from. */
  readonly drawFrom: Seat | null;
  readonly last: (Omit<DrawEvent, 'card'> & { readonly card: Card | null }) | null;
  readonly result: Outcome | null;
}

/** Remove pairs (any two of the same rank); returns remaining hand and the discarded cards. */
function discardPairs(hand: readonly Card[]): { hand: Card[]; discarded: Card[] } {
  const byRank = new Map<string, Card[]>();
  for (const c of hand) byRank.set(c.rank, [...(byRank.get(c.rank) ?? []), c]);
  const keep: Card[] = [];
  const discarded: Card[] = [];
  for (const cards of byRank.values()) {
    const pairs = Math.floor(cards.length / 2) * 2;
    discarded.push(...cards.slice(0, pairs));
    keep.push(...cards.slice(pairs));
  }
  return { hand: keep, discarded };
}

/** Next seat after `from` (exclusive) that still holds cards, or null. */
function nextWithCards(hands: readonly (readonly Card[])[], from: Seat): Seat | null {
  for (let k = 1; k < hands.length; k++) {
    const s = (from + k) % hands.length;
    if ((hands[s]?.length ?? 0) > 0) return s;
  }
  return null;
}

function settle(s: OldMaidState): OldMaidState {
  const holding = s.hands.flatMap((h, i) => (h.length > 0 ? [i] : []));
  if (holding.length > 1) return s;
  // 51 cards minus pairs is always odd, so exactly one holder remains: the Old Maid.
  const loser = holding[0] as Seat;
  const winners = s.hands.map((_, i) => i).filter((i) => i !== loser);
  return { ...s, result: { winners, draw: false, reason: 'old-maid', scores: { [loser]: -1 } } };
}

const firstHolder = (hands: readonly (readonly Card[])[]): Seat =>
  (hands[0]?.length ?? 0) > 0 ? 0 : (nextWithCards(hands, 0) ?? 0);

function drawSource(s: OldMaidState): Seat | null {
  return s.result ? null : nextWithCards(s.hands, s.turn);
}

export const oldMaid: GameDefinition<OldMaidState, OldMaidAction, OldMaidView, OldMaidOptions> = {
  id: 'old-maid',
  name: 'Old Maid',
  rules: [
    'Don’t be left holding the Old Maid. One of the 52 cards, the queen of clubs, is removed, so its partner (the queen of spades) has no pair.',
    'The whole deck is dealt out. Every pair of the same rank is discarded straight away, so you start with only unmatched cards.',
    'On your turn you take one card, face down, from the next player who still has cards. Tap the card you want; the other player’s hand is shuffled each time.',
    'If the card pairs with one in your hand, the pair is discarded automatically.',
    'Players who run out of cards are safe and drop out. The last player holding the queen of spades loses; everyone else wins.',
    'There are no settings for this game. The app enforces everything, so nothing needs agreeing at the table.',
  ],
  minSeats: 2,
  maxSeats: 8,
  defaultOptions: { removedCardId: 'QC#0' },

  setup({ seats, options, rng }) {
    const deck = standardDeck();
    if (!deck.some((c) => c.id === options.removedCardId)) throw new RangeError(`Unknown card ${options.removedCardId}`);
    const { hands } = deal(shuffle(deck.filter((c) => c.id !== options.removedCardId), rng), seats, { perHand: 'all' });
    const cleaned = hands.map(discardPairs);
    const handsOut = cleaned.map((c) => shuffle(c.hand, rng));
    return settle({
      hands: handsOut,
      discards: cleaned.flatMap((c) => c.discarded),
      turn: firstHolder(handsOut),
      last: null,
      result: null,
    });
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a) {
    const from = drawSource(s);
    if (from === null) return { ok: false, reason: 'nobody to draw from' };
    const n = s.hands[from]?.length ?? 0;
    if (!Number.isInteger(a.index) || a.index < 0 || a.index >= n) return { ok: false, reason: `pick a card 0-${n - 1}` };
    return { ok: true };
  },

  apply(s, a, { rng }: { rng: Rng }) {
    const from = drawSource(s) as Seat;
    const source = s.hands[from] as readonly Card[];
    const card = source[a.index] as Card;
    const remaining = source.filter((_, i) => i !== a.index);
    const mine = discardPairs([...(s.hands[s.turn] as readonly Card[]), card]);
    const paired = mine.discarded.length > 0 ? card.rank : null;
    const hands = s.hands.map((h, i) => {
      if (i === from) return shuffle(remaining, rng); // re-fanned: positions carry no information
      if (i === s.turn) return mine.hand;
      return h;
    });
    const settled = settle({
      ...s,
      hands,
      discards: [...s.discards, ...mine.discarded],
      last: { drawer: s.turn, from, card, paired },
    });
    if (settled.result) return settled;
    const turn = (hands[from]?.length ?? 0) > 0 ? from : (nextWithCards(hands, from) as Seat);
    return { ...settled, turn };
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const last = s.last;
    const seesCard = last !== null && (last.paired !== null || you === last.drawer || you === last.from);
    return {
      you,
      hand: you === null ? null : (s.hands[you] ?? null),
      handCounts: s.hands.map((h) => h.length),
      discards: s.discards,
      turn: s.turn,
      drawFrom: drawSource(s),
      last: last === null ? null : { ...last, card: seesCard ? last.card : null },
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    const from = drawSource(s);
    if (seat !== s.turn || from === null) return [];
    return Array.from({ length: s.hands[from]?.length ?? 0 }, (_, index) => ({ type: 'draw', index }) as const);
  },
};
